import Image from "next/image";
import Quote from "@/assets/Quote";
import { cn } from "@/lib/utils";

const QuoteCard = ({
  description,
  descriptionTextColor,
  descriptionBackgroundColor,
  variant,
  showQuoteIcon = true,
  quoteIcon = "",
  quoteIconAlt = "",
}: {
  description: string;
  descriptionTextColor?: string;
  descriptionBackgroundColor?: string;
  variant?: string;
  showQuoteIcon?: boolean;
  quoteIcon?: string;
  quoteIconAlt?: string;
}) => {
  const isV2 = variant === "variant_2";
  return (
    <div
      className={cn(
        "flex flex-col p-3 gap-3 rounded-[20px] overflow-hidden",
        // Below 800px (OnlineMasterClassCard's mobile branch) the card has no width of
        // its own, so a long quote stretches it across the whole carousel slide and
        // neighbouring slides touch while swiping. Cap it so a gutter always remains.
        // Viewport units, not %, because every ancestor here is shrink-to-fit.
        "max-[799px]:max-w-[min(280px,calc(100vw-80px))]",
        "sm:p-[14px] sm:gap-[18px] sm:w-[325px] sm:rounded-lg",
        "md:w-[340px] lg:w-[408px]",
        "xl:w-[608px] xl:rounded-[20px]",
        // Variant 2 height hugs the text (no fixed height / clamp).
        // Variant 1 keeps its original fixed heights so existing tenants are unchanged.
        !isV2 && "sm:h-[140px] md:h-[156px] lg:h-[168px] xl:h-[192px]",
        // Variant 2 matches the Figma quote: Poppins SemiBold 14px (mobile) → Regular 18px (desktop).
        isV2
          ? "font-poppins font-semibold text-[14px] leading-normal min-[800px]:font-normal min-[800px]:text-[18px]"
          : cn(
              "font-wf-visual-sans leading-150 -tracking-014",
              "text-heading-9-semibold",
              "sm:text-heading-9-regular",
              "md:text-heading-8-regular",
              "lg:text-body-1-regular",
              "xl:text-new-body-24-regular",
            ),
      )}
      style={{
        backgroundColor:
          descriptionBackgroundColor || "var(--color-primary-main)",
        color: descriptionTextColor || "#ffffff",
      }}
    >
      {showQuoteIcon ? (
        quoteIcon ? (
          <Image
            src={quoteIcon}
            alt={quoteIconAlt || "Quote icon"}
            width={24}
            height={24}
            className="shrink-0 object-contain xs:w-5 xs:h-5 sm:w-6 sm:h-6"
          />
        ) : (
          <Quote className={cn("xs:w-4 xs:h-4", "sm:w-[14px] sm:h-3")} />
        )
      ) : null}
      <p className={cn(!isV2 && "xs:line-clamp-4", !isV2 && "type-body-sm")}>
        {description}
      </p>
    </div>
  );
};

export default QuoteCard;
